import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import FeeStructureUploadService from "../../../services/FeeStructureUpload.service";
import { DataGrid } from "@mui/x-data-grid";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";

const FeeStructureApprove = () => {
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  const clientId = Number(user.client_id);
  const collegeId = Number(user.college_id);
  const modifiedBy = user.user_email || user.user_name || null;

  const [tabValue, setTabValue] = useState(0);

  const [summaryRows, setSummaryRows] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);

  const [selectedBatch, setSelectedBatch] = useState(null);
  const [dialogRows, setDialogRows] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);

  const [actionStatus, setActionStatus] = useState("APPROVED");

  const currentStatus = tabValue === 0 ? "DRAFT" : "APPROVED";

  // ---------------------------------------------------------
  // LOAD PENDING / APPROVED BATCH SUMMARY
  // ---------------------------------------------------------
  const loadSummary = useCallback(async () => {
    if (!clientId || !collegeId) return;

    try {
      setSummaryLoading(true);

      const response = await FeeStructureUploadService.getWorkflowSummary(
        clientId,
        collegeId,
        currentStatus
      );

      const rows = response.data?.rows || [];

      setSummaryRows(
        rows.map((row) => ({
          ...row,
          id: Number(row.admittedbatch_id),
          total_rows: Number(row.total_rows || 0),
        }))
      );
    } catch (error) {
      console.error("Workflow summary error:", error);

      Swal.fire({
        icon: "error",
        title: "Unable to load fee structures",
        text: error.response?.data?.message || error.message || "Please try again.",
      });
    } finally {
      setSummaryLoading(false);
    }
  }, [clientId, collegeId, currentStatus]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // ---------------------------------------------------------
  // OPEN DETAILS DIALOG
  // ---------------------------------------------------------
  const openDetails = async (batch, status, action) => {
    setSelectedBatch(batch);
    setActionStatus(action);
    setDialogRows([]);
    setSelectionModel([]);
    setDialogOpen(true);

    try {
      setDialogLoading(true);

      const response = await FeeStructureUploadService.getWorkflowFeeStructures(
        clientId,
        collegeId,
        batch.admittedbatch_id,
        status
      );

      const rows = response.data?.rows || [];

      setDialogRows(
        rows.map((row) => ({
          ...row,
          id: String(row.fee_structure_id),
          fee_structure_id: String(row.fee_structure_id),
        }))
      );
    } catch (error) {
      console.error("Fee structure details error:", error);

      setDialogOpen(false);

      Swal.fire({
        icon: "error",
        title: "Unable to load fee structure details",
        text: error.response?.data?.message || error.message || "Please try again.",
      });
    } finally {
      setDialogLoading(false);
    }
  };

  // ---------------------------------------------------------
  // APPROVE / REJECT SELECTED ROWS
  // ---------------------------------------------------------
  const handleConfirmWorkflow = async () => {
    if (!selectionModel.length) {
      Swal.fire({
        icon: "warning",
        title: "Select fee structures",
        text: "Please select at least one fee structure.",
      });
      return;
    }

    const actionText = actionStatus === "APPROVED" ? "approve" : "reject";

    const confirmation = await Swal.fire({
      title: `Confirm ${actionText}?`,
      text: `You have selected ${selectionModel.length} fee structure(s).`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: actionStatus === "APPROVED" ? "Yes, Approve" : "Yes, Reject",
      cancelButtonText: "Cancel",
      confirmButtonColor: actionStatus === "APPROVED" ? "#4CAF50" : "#d32f2f",
      target: document.body,
      didOpen: () => {
        const container = document.querySelector(".swal2-container");

        if (container) {
          container.style.zIndex = "20000";
        }
      },
    });

    if (!confirmation.isConfirmed) return;

    try {
      setDialogLoading(true);

      const response = await FeeStructureUploadService.updateWorkflowStatus(clientId, collegeId, {
        fee_structure_ids: selectionModel.map(String),
        workflow_status: actionStatus,
        modified_by: modifiedBy,
      });

      const result = response.data || {};

      setDialogOpen(false);
      setSelectionModel([]);

      await Swal.fire({
        icon: "success",
        title: "Successful",
        text:
          result.message || `${result.updatedCount || 0} fee structure(s) updated successfully.`,
        confirmButtonColor: "#4CAF50",
      });

      await loadSummary();
    } catch (error) {
      console.error("Workflow update error:", error);

      Swal.fire({
        icon: "error",
        title: "Update failed",
        text:
          error.response?.data?.message ||
          error.message ||
          "Unable to update fee structure status.",
      });
    } finally {
      setDialogLoading(false);
    }
  };

  // ---------------------------------------------------------
  // SUMMARY GRID COLUMNS
  // ---------------------------------------------------------
  const summaryColumns = useMemo(
    () => [
      {
        field: "serial",
        headerName: "S. No.",
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          return params.api.getRowIndex(params.id) + 1;
        },
      },
      {
        field: "admittedbatch",
        headerName: "Admitted Batch",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "total_rows",
        headerName: "No. of Fee Structures",
        width: 190,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "actions",
        headerName: "Actions",
        width: tabValue === 0 ? 300 : 140,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          if (tabValue === 1) {
            return (
              <Button
                size="small"
                variant="contained"
                color="info"
                onClick={() => openDetails(params.row, "APPROVED", "APPROVED")}
                sx={{
                  backgroundColor: "#4CAF50",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#388E3C",
                    color: "#fff",
                  },
                  textTransform: "none",
                }}
              >
                View
              </Button>
            );
          }

          return (
            <Box display="flex" gap={1}>
              <Button
                size="small"
                variant="contained"
                sx={{
                  backgroundColor: "#4CAF50",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#388E3C",
                    color: "#fff",
                  },
                  textTransform: "none",
                }}
                onClick={() => openDetails(params.row, "DRAFT", "APPROVED")}
              >
                Approve
              </Button>

              <Button
                size="small"
                variant="contained"
                sx={{
                  backgroundColor: "#d32f2f",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#b71c1c",
                    color: "#fff",
                  },
                  textTransform: "none",
                }}
                onClick={() => openDetails(params.row, "DRAFT", "REJECTED")}
              >
                Reject
              </Button>
            </Box>
          );
        },
      },
    ],
    [summaryRows, tabValue]
  );

  // ---------------------------------------------------------
  // DETAILS GRID COLUMNS
  // ---------------------------------------------------------
  const detailColumns = useMemo(
    () => [
      {
        field: "fee_structure_id",
        headerName: "ID",
        width: 100,
      },
      {
        field: "program_name",
        headerName: "Program",
        minWidth: 150,
        flex: 1,
      },
      {
        field: "branch_name",
        headerName: "Branch",
        minWidth: 180,
        flex: 1,
      },
      {
        field: "seat_type",
        headerName: "Seat",
        width: 90,
      },
      {
        field: "fee_category_name",
        headerName: "Fee Category",
        minWidth: 150,
        flex: 1,
      },
      {
        field: "fee_type_name",
        headerName: "Fee Type",
        minWidth: 150,
        flex: 1,
      },
      {
        field: "fee_amount",
        headerName: "Fee Amount",
        width: 130,
        align: "right",
        headerAlign: "right",
        valueFormatter: (params) =>
          Number(params.value || 0).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
      },
      {
        field: "term1",
        headerName: "Term 1",
        width: 110,
        align: "right",
        headerAlign: "right",
      },
      {
        field: "term2",
        headerName: "Term 2",
        width: 110,
        align: "right",
        headerAlign: "right",
      },
      {
        field: "term3",
        headerName: "Term 3",
        width: 110,
        align: "right",
        headerAlign: "right",
      },
      {
        field: "workflow_status",
        headerName: "Status",
        width: 120,
      },
    ],
    []
  );

  return (
    <Box p={2}>
      <Typography variant="h5" fontWeight="bold" mb={2} color="blue">
        FEE STRUCTURE APPROVAL
      </Typography>

      <Tabs
        value={tabValue}
        onChange={(event, value) => setTabValue(value)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minHeight: 66,
          mb: 2,
          borderBottom: "1px solid #e0e0e0",

          "& .MuiTabs-indicator": {
            height: 4,
            borderRadius: "4px 4px 0 0",
            backgroundColor: "#455a64",
          },

          "& .MuiTab-root": {
            minHeight: 60,
            minWidth: { xs: 155, sm: 220 },
            px: 2.5,
            py: 1.5,
            mr: 1,
            borderRadius: "8px 8px 0 0",
            fontWeight: 700,
            fontSize: { xs: 13, sm: 15 },
            textTransform: "none",
            opacity: 1,
            transition: "background-color 0.2s ease",
          },

          /* Pending - Light Orange */
          "& .MuiTab-root:nth-of-type(1)": {
            backgroundColor: "#FFF3E0",
            color: "#9A5300",
            "&:hover": {
              backgroundColor: "#FFE0B2",
            },
          },

          /* Approved - Light Green */
          "& .MuiTab-root:nth-of-type(2)": {
            backgroundColor: "#E8F5E9",
            color: "#1B5E20",
            "&:hover": {
              backgroundColor: "#C8E6C9",
            },
          },

          "& .MuiTab-root.Mui-selected": {
            fontWeight: 800,
            boxShadow: "inset 0 -4px 0 #455a64",
          },

          "& .MuiTabs-scrollButtons": {
            color: "#1976d2",
          },
        }}
      >
        <Tab label="Pending Fee Structure" />
        <Tab label="Approved FeeStructure" />
      </Tabs>

      <Box
        sx={{
          height: 500,
          width: "100%",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#f5f5f5",
          },
        }}
      >
        <DataGrid
          rows={summaryRows}
          columns={summaryColumns}
          loading={summaryLoading}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          autoHeight={false}
        />
      </Box>

      {/* -------------------------------------------------
                DETAILS / APPROVAL DIALOG
            ------------------------------------------------- */}
      <Dialog
        open={dialogOpen}
        onClose={(event, reason) => {
          if (dialogLoading) return;

          if (reason === "backdropClick") return;

          setDialogOpen(false);
        }}
        fullWidth
        maxWidth="xl"
        scroll="paper"
        sx={{
          zIndex: 1300,
        }}
        PaperProps={{
          sx: {
            minHeight: "80vh",
            maxHeight: "95vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#f5f5f5",
            borderBottom: "1px solid #ddd",
            py: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {selectedBatch?.admittedbatch || "Fee Structures"}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {actionStatus === "APPROVED"
                ? "Select fee structures to approve."
                : "Select fee structures to reject."}
            </Typography>
          </Box>

          <IconButton
            onClick={() => setDialogOpen(false)}
            disabled={dialogLoading}
            aria-label="Close dialog"
            sx={{
              color: "#ffebee",
              backgroundColor: "#c53f3f",
              "&:hover": {
                backgroundColor: "#d32f2f",
                color: "#ffebee",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            minHeight: 400,
            overflow: "auto",
            pt: 2,
          }}
        >
          <Box sx={{ height: 500, width: "100%" }}>
            <DataGrid
              rows={dialogRows}
              columns={detailColumns}
              loading={dialogLoading}
              checkboxSelection
              selectionModel={selectionModel}
              onSelectionModelChange={(newSelection) => setSelectionModel(newSelection.map(String))}
              disableSelectionOnClick
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              autoHeight={false}
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f5f5f5",
                  fontWeight: "bold",
                },
              }}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" mt={1}>
            Selected: {selectionModel.length} / {dialogRows.length}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            borderTop: "1px solid #ddd",
          }}
        >
          <Button
            variant="contained"
            onClick={() => setDialogOpen(false)}
            disabled={dialogLoading}
            sx={{
              backgroundColor: "#d32f2f",
              color: "#ffebee",
              "&:hover": { backgroundColor: "#b71c1c" },
              color: "#ffebee",
              textTransform: "none",
            }}
          >
            Close
          </Button>

          <Button
            variant="contained"
            onClick={handleConfirmWorkflow}
            disabled={dialogLoading || selectionModel.length === 0}
            color={actionStatus === "APPROVED" ? "success" : "error"}
            sx={{
              backgroundColor: "#2f34d3",
              color: "#ffebee",
              "&:hover": { backgroundColor: "#1a237e" },
              color: "#ffebee",
              textTransform: "none",
            }}
          >
            {actionStatus === "APPROVED" ? "Confirm Approved" : "Confirm Rejected"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeeStructureApprove;
